import { type Context } from "@nuxt/types";
import {
  type BrowserTracker,
  addGlobalContexts,
  newTracker,
  trackPageView,
  trackSelfDescribingEvent,
} from "@snowplow/browser-tracker";
import { type TrackerConfiguration } from "@snowplow/browser-tracker-core";

import { isAuth0UserToken } from "@/schemes/auth0";
import { getExperimentsStore } from "@/store/experiments";
import { getLtksStore } from "@/store/ltks";
import { getProfilesStore } from "@/store/profiles";
import {
  FEATURE_FLAG_ACTIVE,
  FLAG_IMPRESSIONS_VISIT_POST,
  FLAG_IMPRESSIONS_VISIT_SHOP,
} from "@/utils/experiments/constants";
import { Schemas } from "@/utils/snowplow/enums";
import { type Impression, type PromotionEntity, type ShopEntity } from "@/utils/snowplow/types";
import { fromV3, safeConvert, toV1 } from "@/utils/transform/ordered-uuid";

import { createUserMetaEntity } from "./factory";

declare module "vue/types/vue" {
  interface Vue {
    $snowplow: Snowplow;
  }
}

declare module "@nuxt/types" {
  interface NuxtConfig {
    snowplow?: {
      server: string;
      trackerName: string;
      trackerConfig: TrackerConfiguration;
    };
  }

  interface Context {
    $snowplow: Snowplow;
  }
}

export interface PatchedBrowserTracker extends BrowserTracker {
  setEventMethod: (method: "post" | "get" | "beacon") => void;
}

interface MaybeOldBrowserDocument extends Omit<Document, "visibilityState"> {
  visibilityState?: DocumentVisibilityState;
}

export default class Snowplow {
  public tracker: PatchedBrowserTracker;
  public context: Context;
  private experiments: Record<string, boolean> = {};

  constructor(context: Context) {
    this.context = context;
    const { trackerName, server, trackerConfig } = this.context.$config.snowplow;

    this.tracker = newTracker(trackerName, server, trackerConfig) as PatchedBrowserTracker;

    const userId =
      this.context.$auth.user === null
        ? undefined
        : isAuth0UserToken(this.context.$auth.user)
        ? this.context.$auth.user["http://shopltk.com/profile"].sigilUserId
        : this.context.$auth.user?.userId;

    if (userId) {
      this.tracker.setUserId(userId);
    }

    // Proactively fetch flags to be used by events
    this._fetchFlags();

    // Append entities in array to ALL events via global context
    addGlobalContexts([createUserMetaEntity({ vid: this.context.$branch?.vid })]);

    /**
     *  Flush queue when the user navigates to a new page, switches tabs, closes the tab, minimizes
     *  or closes the browser, or, on mobile, switches from the browser to a different app. Using the
     *  `navigator.sendBeacon()` API is only somewhat reliable, but it is our only option. A quick
     *  summary of the problem can be found at the below link:
     *
     *  https://www.speedkit.com/blog/unload-beacon-reliability-benchmarking-strategies-for-minimal-data-loss/
     *
     *  A more comprehensive write-up of the issue, strategies, and what not can be found here:
     *
     *  https://nicj.net/beaconing-in-practice/#beaconing-tldr
     *
     *  We avoid using `window` and `onvisibilitychange` as they're both not fully compatible with
     *  all browsers (https://caniuse.com/mdn-api_document_visibilitychange_event). So, we must sniff
     *  the browser and pick the appropriate event handler. See the previous link for info on which
     *  browsers support which methods, and why this check is necessary.
     *
     *  Lastly, we patched the @snowplow/browser-tracker-core SDK to include a `setEventMethod()`
     *  which simply allows you to toggle the protocol being used to send events to the collector.
     *  It also flushes the queue on toggle. This being done to get the `sendBeacon()` fired as early
     *  as possible. We needed the toggle as the Beacon API is intended for smaller less frequent requests.
     *  In testing we were hitting the APIs queue limits. So, to alleviate that we start with `post` as
     *  the primary method, and only fallback to `beacon` when we need to flush the queue.
     */
    const visibilityChangeHandler = () =>
      document.visibilityState === "hidden"
        ? this.tracker.setEventMethod("beacon")
        : this.tracker.setEventMethod("post");
    const pagehideHandler = () => this.tracker.setEventMethod("beacon");
    const pageshowHandler = () => this.tracker.setEventMethod("post");

    if ("visibilityState" in (document as MaybeOldBrowserDocument) && document.visibilityState !== undefined) {
      document.addEventListener("visibilitychange", visibilityChangeHandler, { capture: true });
    } else {
      document.addEventListener("pagehide", pagehideHandler, { capture: true });
      document.addEventListener("pageshow", pageshowHandler, { capture: true });
    }
  }

  private _fetchFlags(): void {
    const experimentsStore = getExperimentsStore(this.context.store);
    experimentsStore.fetchFeatureFlag({ flagName: FLAG_IMPRESSIONS_VISIT_POST });
    experimentsStore.fetchFeatureFlag({ flagName: FLAG_IMPRESSIONS_VISIT_SHOP });
    const visitPost = experimentsStore.flag(FLAG_IMPRESSIONS_VISIT_POST);
    const visitShop = experimentsStore.flag(FLAG_IMPRESSIONS_VISIT_SHOP);
    this.experiments[FLAG_IMPRESSIONS_VISIT_POST] = Boolean(visitPost?.value === FEATURE_FLAG_ACTIVE);
    this.experiments[FLAG_IMPRESSIONS_VISIT_SHOP] = Boolean(visitShop?.value === FEATURE_FLAG_ACTIVE);
  }

  public trackEvent(data: Impression): void {
    trackSelfDescribingEvent(data);
  }

  public trackPageView(): void {
    switch (this.context.route.name) {
      case "explore-displayName":
        this.trackShopVisit();
        break;
      case "explore-displayName-posts-id":
        this.trackPostVisit();
        break;
      case "explore-displayName-productsets-id":
        this.trackPromotionVisit();
        break;
      case "explore-displayName-giftguides-id":
        this.trackPromotionVisit();
        break;
      default:
        this.tracker.trackPageView();
    }
  }

  public trackShopVisit(): void {
    if (!this.experiments[FLAG_IMPRESSIONS_VISIT_SHOP]) {
      return;
    }

    const displayName = this.context.params.displayName;
    const profilesStore = getProfilesStore(this.context.store);
    const data: ShopEntity = { id: profilesStore.getIdByDisplayName(displayName) ?? "" };

    trackPageView({ context: [{ schema: Schemas.ShopEntity, data }] });
  }

  public trackPostVisit(): void {
    if (!this.experiments[FLAG_IMPRESSIONS_VISIT_POST]) {
      return;
    }

    const id = this.context.params.id;
    const ltksStore = getLtksStore(this.context.store);
    const data: PromotionEntity = { id, profile_id: ltksStore.ltks[id]?.profileId ?? "" };

    trackPageView({ context: [{ schema: Schemas.PromotionEntity, data }] });
  }

  public trackPromotionVisit(): void {
    const id = this.context.params.id;
    const convertedId = safeConvert(id, fromV3, toV1);
    const displayName = this.context.params.displayName;
    const profilesStore = getProfilesStore(this.context.store);
    const data: PromotionEntity = {
      id: convertedId,
      profile_id: profilesStore.getIdByDisplayName(displayName) ?? "",
    };

    trackPageView({ context: [{ schema: Schemas.PromotionEntity, data }] });
  }
}
